import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { FaEye, FaEdit, FaTrash, FaPlus, FaSearch } from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import { confirmAlert } from "react-confirm-alert";

import Modal from "../../../components/Modal/Modal";
import ErrorBoundaryRoutes from "../../../components/shared/ErrorBoundaryRoutes";
import { gbState } from "../../../components/shared/GlobalState";
import placeholderImage from "../../../images/placeholder.png";

import EmployeeView from "./EmployeeView";

const EmployeeListChild = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [{ errorLogApi, placeHolderText }] = useAtom(gbState);

  const getDataFromServer = async () => {
    const userToken = localStorage.getItem("token");
    const urlLink = `${process.env.REACT_APP_API_URL}/employee/`;

    const config = {
      method: "GET",
      url: urlLink,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    try {
      const response = await axios(config);
      const oldData = response.data.employees;
      const updatedData = oldData.map((data) => ({
        ...data,
        id: data._id,
        name: `${data.first_name} ${data.last_name}`,
        email: data.email,
        designation: data.designation,
        mobile: data.mobile,
        gender: data.gender,
        date_of_birth: data.date_of_birth,
        bank_name: data.bank_detail[0]?.bank_name,
        branch_name: data.bank_detail[0]?.branch_name,
        ifsc_code: data.bank_detail[0]?.ifsc_code,
        account_number: data.bank_detail[0]?.account_number,
      }));

      setData(updatedData);
    } catch (err) {
      console.log("error api call ", err.response?.data?.message);
      errorLogApi(err, "EmployeeListChild", "/employees");
    }
  };

  useEffect(() => {
    getDataFromServer();
  }, []);

  const handleDelete = (row) => {
    confirmAlert({
      title: "Confirm Deletion",
      message: "Are you sure you want to delete this Employee?",
      buttons: [
        {
          label: "Yes",
          onClick: () => deleteRecord(row),
        },
        {
          label: "No",
        },
      ],
    });
  };

  const deleteRecord = async (row) => {
    const userToken = localStorage.getItem("token");
    const config = {
      headers: { Authorization: `Bearer ${userToken}` },
    };

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/employee/delete/${row._id}`,
        config
      );
      toast.success("Employee Deleted Successfully.");
      getDataFromServer();
    } catch (err) {
      errorLogApi(err, "EmployeeListChild", "/employees");
    }
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const filteredData = useMemo(() => {
    return data.filter((employee) =>
      Object.values(employee).some((value) =>
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [data, searchTerm]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  return (
    <div className="px-4 mx-auto w-full max-w-7xl sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Employee List</h2>
        <Button
          variant="contained"
          color="primary"
          startIcon={<FaPlus />}
          onClick={() => navigate("/employeeadd")}
        >
          Add Employee / Volunteer
        </Button>
      </div>

      <div className="mb-4">
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search employees..."
          InputProps={{
            startAdornment: <FaSearch className="mr-2 text-gray-400" />,
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Image</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Designation</TableCell>
              <TableCell>Mobile</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <TableRow key={row._id}>
                <TableCell>
                  <div className="flex items-center space-x-1">
                    <Tooltip title="View details">
                      <IconButton
                        size="small"
                        onClick={() => {
                          toggleModal();
                          setCurrentRecord(row);
                        }}
                      >
                        <FaEye className="text-blue-600" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate("/employee/update", { state: row })
                        }
                      >
                        <FaEdit className="text-gray-600" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(row)}
                      >
                        <FaTrash className="text-red-600" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </TableCell>
                <TableCell>
                  <img
                    src={
                      row.image
                        ? `${process.env.REACT_APP_REPORTS_DOWNLOAD_URL}uploads/employee/${row.image}`
                        : placeholderImage
                    }
                    alt="Employee"
                    className="w-10 h-10 rounded-full"
                    crossOrigin="anonymous"
                  />
                </TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.email}</TableCell>
                <TableCell>{row.designation}</TableCell>
                <TableCell>{row.mobile}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination controls */}
      <div className="flex justify-between items-center mt-4">
        <div>
          <span>Rows per page: </span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
          >
            {[5, 10, 25, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Button
            disabled={page === 0}
            onClick={() => setPage((prev) => prev - 1)}
          >
            Previous
          </Button>
          <Button
            disabled={paginatedData.length < rowsPerPage}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Modal */}
      <ErrorBoundaryRoutes route="/employees/" page="ModalView">
        {showModal && (
          <Modal onClose={() => setShowModal(false)}>
            <EmployeeView currentRecord={currentRecord} />
          </Modal>
        )}
      </ErrorBoundaryRoutes>
    </div>
  );
};

export default EmployeeListChild;
